/**
 * 数字人作品 · 本地 + 云端持久化（刷新不丢、后台可续轮询）
 */
import {
  queryImageHumanTask,
  pollImageHumanUntilDone,
  fetchImageHumanTaskRecords,
  saveImageHumanTaskRecord,
  isLikelyVideoUrl
} from '@/utils/digitalHumanApi'
import { isLoggedInUser } from '@/utils/loginGate'

export const DIGITAL_HUMAN_TASKS_KEY = 'plus_image_human_tasks'
const LEGACY_TASKS_KEY = DIGITAL_HUMAN_TASKS_KEY

const IN_PROGRESS = new Set(['pending', 'processing', 'submitted', 'queued', 'queue', 'waiting'])

export function getCurrentUserId() {
  try {
    const raw = localStorage.getItem('user') || sessionStorage.getItem('user') || ''
    if (!raw) return 'guest'
    const user = JSON.parse(raw)
    const id = user && (user.id ?? user.user_id ?? user.uid)
    return id != null && String(id).trim() !== '' ? String(id) : 'guest'
  } catch (e) {
    return 'guest'
  }
}

function readTasksFromStorageKey(key) {
  try {
    const raw = readRawStorage(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((t) => t && t.taskId)
  } catch (e) {
    return []
  }
}

function filterTasksByCurrentUser(tasks) {
  const uid = getCurrentUserId()
  if (uid === 'guest') {
    return (tasks || []).filter((t) => {
      const tu = String(t.userId || t.user_id || 'guest')
      return tu === 'guest' || !t.userId
    })
  }
  return (tasks || []).filter((t) => {
    const tu = t.userId != null || t.user_id != null ? String(t.userId || t.user_id) : uid
    return tu === uid
  })
}

export function getDigitalHumanStorageKey() {
  return `${DIGITAL_HUMAN_TASKS_KEY}_${getCurrentUserId()}`
}

function readRawStorage(key) {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || ''
  } catch (e) {
    return ''
  }
}

function writeRawStorage(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    /* ignore */
  }
  try {
    sessionStorage.setItem(key, value)
  } catch (e) {
    /* ignore */
  }
}

function migrateLegacyTasksIfNeeded() {
  const key = getDigitalHumanStorageKey()
  if (readRawStorage(key)) return
  const legacy = readRawStorage(LEGACY_TASKS_KEY)
  if (!legacy) return
  writeRawStorage(key, legacy)
}

export function isTaskInProgress(status) {
  return IN_PROGRESS.has(String(status || '').toLowerCase())
}

/** 仅保留有效视频结果 URL，排除人物封面等输入图 */
export function sanitizeVideoResultUrl(url, cover = '') {
  const u = String(url || '').trim()
  if (!u) return ''
  const c = String(cover || '').trim()
  if (c && u === c) return ''
  return isLikelyVideoUrl(u) ? u : ''
}

export function loadDigitalHumanTasks() {
  const uid = getCurrentUserId()
  if (isLoggedInUser() && uid !== 'guest') {
    migrateLegacyTasksIfNeeded()
    const list = readTasksFromStorageKey(getDigitalHumanStorageKey())
    return filterTasksByCurrentUser(list).sort((a, b) => {
      const ta = Date.parse(a.updatedAt || a.createdAt || 0) || 0
      const tb = Date.parse(b.updatedAt || b.createdAt || 0) || 0
      return tb - ta
    })
  }
  const guestKey = `${DIGITAL_HUMAN_TASKS_KEY}_guest`
  const guestList = readTasksFromStorageKey(guestKey)
  const legacyList = guestList.length ? [] : readTasksFromStorageKey(LEGACY_TASKS_KEY)
  const map = new Map()
  ;[...legacyList, ...guestList].forEach((t) => {
    map.set(String(t.taskId), { ...t })
  })
  return filterTasksByCurrentUser([...map.values()]).sort((a, b) => {
    const ta = Date.parse(a.updatedAt || a.createdAt || 0) || 0
    const tb = Date.parse(b.updatedAt || b.createdAt || 0) || 0
    return tb - ta
  })
}

export function saveDigitalHumanTasks(tasks) {
  const payload = JSON.stringify(Array.isArray(tasks) ? tasks.slice(0, 50) : [])
  writeRawStorage(getDigitalHumanStorageKey(), payload)
}

/** 从本地列表移除一条作品 */
export function removeDigitalHumanTask(taskId) {
  const id = String(taskId || '').trim()
  if (!id) return loadDigitalHumanTasks()
  const list = loadDigitalHumanTasks().filter((t) => String(t.taskId) !== id)
  saveDigitalHumanTasks(list)
  return [...list]
}

function syncTaskToServer(task) {
  if (!task || !task.taskId || !isLoggedInUser()) return
  saveImageHumanTaskRecord(task).catch(() => {})
}

function taskCompletionScore(task) {
  if (!task) return 0
  const status = String(task.status || '').toLowerCase()
  const hasResult = !!sanitizeVideoResultUrl(task.resultUrl || task.shuzirenUrl, task.cover)
  if (status === 'failed') return 1
  if (status === 'completed' && hasResult) return 4
  if (status === 'completed') return 3
  if (hasResult) return 3
  if (isTaskInProgress(status)) return 2
  return 1
}

function mergeTaskRow(prev, next) {
  if (!prev) return { ...next }
  if (!next) return { ...prev }
  const scoreA = taskCompletionScore(prev)
  const scoreB = taskCompletionScore(next)
  if (scoreA !== scoreB) {
    // 完成度高的记录优先，避免本地「生成中」盖掉服务端「已完成」
    return scoreA > scoreB ? { ...next, ...prev } : { ...prev, ...next }
  }
  const ta = Date.parse(prev.updatedAt || prev.createdAt || 0) || 0
  const tb = Date.parse(next.updatedAt || next.createdAt || 0) || 0
  return ta >= tb ? { ...next, ...prev } : { ...prev, ...next }
}

export function upsertDigitalHumanTask(patch) {
  const taskId = String(patch?.taskId || patch?.id || '').trim()
  if (!taskId) return loadDigitalHumanTasks()
  const list = loadDigitalHumanTasks()
  const idx = list.findIndex((t) => String(t.taskId) === taskId)
  const prev = idx >= 0 ? list[idx] : null

  if (prev) {
    const prevStatus = String(prev.status || '').toLowerCase()
    const prevHasResult = !!sanitizeVideoResultUrl(prev.resultUrl || prev.shuzirenUrl, prev.cover)
    const nextStatus = String(patch.status || '').toLowerCase()
    if (patch.resultUrl) {
      patch.resultUrl = sanitizeVideoResultUrl(patch.resultUrl, patch.cover || prev.cover) || undefined
    }
    if (nextStatus === 'completed' || patch.resultUrl) {
      // 允许升级为已完成
    } else if (prevStatus === 'completed' && prevHasResult) {
      if (isTaskInProgress(nextStatus)) {
        delete patch.status
      }
      if (!patch.resultUrl) {
        patch.resultUrl = sanitizeVideoResultUrl(prev.resultUrl || prev.shuzirenUrl, prev.cover) || undefined
      }
    } else if (prevStatus === 'completed' && isTaskInProgress(nextStatus)) {
      delete patch.status
    }
  }

  const row = mergeTaskRow(prev, {
    ...(prev || {}),
    ...patch,
    taskId,
    userId: getCurrentUserId(),
    updatedAt: new Date().toISOString()
  })
  if (!row.createdAt) row.createdAt = row.updatedAt

  const resultUrl = sanitizeVideoResultUrl(row.resultUrl || row.shuzirenUrl, row.cover)
  const rowStatus = String(row.status || '').toLowerCase()
  if (resultUrl && rowStatus !== 'failed') {
    row.status = 'completed'
    row.resultUrl = resultUrl
    delete row.shuzirenUrl
  } else if (rowStatus === 'completed') {
    row.status = 'completed'
    if (resultUrl) {
      row.resultUrl = resultUrl
    } else {
      delete row.resultUrl
      delete row.shuzirenUrl
    }
  } else if (row.resultUrl || row.shuzirenUrl) {
    delete row.resultUrl
    delete row.shuzirenUrl
  }

  if (idx >= 0) list.splice(idx, 1, row)
  else list.unshift(row)
  saveDigitalHumanTasks(list)
  // 进行中也同步，避免「提交后右侧空白、刷新才出现」
  const syncStatus = String(row.status || '').toLowerCase()
  const syncHasResult = !!sanitizeVideoResultUrl(row.resultUrl, row.cover)
  if (
    syncStatus === 'completed' ||
    syncHasResult ||
    syncStatus === 'failed' ||
    isTaskInProgress(syncStatus) ||
    syncStatus === 'processing' ||
    syncStatus === 'pending'
  ) {
    syncTaskToServer(row)
  }
  return [...list]
}

/** 合并服务端与本地，服务端已完成优先于本地进行中的 updatedAt */
export function mergeTaskRecords(localTasks, remoteTasks) {
  const map = new Map()
  ;(localTasks || []).forEach((t) => {
    if (t && t.taskId) {
      map.set(String(t.taskId), { ...t })
    }
  })
  ;(remoteTasks || []).forEach((t) => {
    if (t && t.taskId) {
      const id = String(t.taskId)
      map.set(id, mergeTaskRow(map.get(id), t))
    }
  })
  return [...map.values()].sort((a, b) => {
    const ta = Date.parse(a.updatedAt || a.createdAt || 0) || 0
    const tb = Date.parse(b.updatedAt || b.createdAt || 0) || 0
    return tb - ta
  })
}

/** 从服务端拉取并合并写入本地（仅当前登录用户） */
export async function pullDigitalHumanTasksFromServer() {
  if (!isLoggedInUser()) {
    return []
  }
  try {
    const remote = await fetchImageHumanTaskRecords(50)
    const local = loadDigitalHumanTasks()
    const merged = mergeTaskRecords(local, remote)
    saveDigitalHumanTasks(merged)
    return merged
  } catch (e) {
    return loadDigitalHumanTasks()
  }
}

export function taskMatchesTab(task, tab) {
  const status = String(task?.status || '').toLowerCase()
  if (tab === 'all') return true
  if (tab === '0') return isTaskInProgress(status)
  if (tab === '1') return status === 'completed' && !!sanitizeVideoResultUrl(task.resultUrl || task.shuzirenUrl, task.cover)
  return true
}

export function taskToProjectCard(task) {
  const resultUrl = sanitizeVideoResultUrl(task.resultUrl || task.shuzirenUrl, task.cover)
  let status = String(task?.status || '').toLowerCase()
  if (resultUrl && status !== 'failed') status = 'completed'
  if (!resultUrl && status === 'completed') status = 'completed'
  return {
    id: 'local-' + task.taskId,
    taskId: task.taskId,
    title: task.title || '数字人作品',
    time: task.createdAt || task.updatedAt || '',
    cover: task.cover || '',
    shuzirenUrl: resultUrl,
    _local: true,
    _status: status,
    _driveMode: task.driveMode || 'full',
    _error: task.error || ''
  }
}

/** 本地 + 远端合并去重 */
export function mergeProjectLists(localTasks, remoteProjects, tab) {
  const cards = []
  const seen = new Set()

  ;(localTasks || [])
    .filter((t) => taskMatchesTab(t, tab))
    .forEach((t) => {
      const card = taskToProjectCard(t)
      seen.add(String(t.taskId))
      if (card.shuzirenUrl) seen.add(card.shuzirenUrl)
      cards.push(card)
    })

  ;(remoteProjects || []).forEach((p) => {
    const url = p.shuzirenUrl || ''
    const id = String(p.id || p.uuid || '')
    if (url && seen.has(url)) return
    if (id && seen.has(id)) return
    if (tab === '0' && p.status === 1) return
    if (tab === '1' && p.status === 0) return
    cards.push({ ...p, _local: false })
  })

  return cards.sort((a, b) => {
    const ta = Date.parse(a.time || a.updatedAt || 0) || 0
    const tb = Date.parse(b.time || b.updatedAt || 0) || 0
    return tb - ta
  })
}

/** 刷新后：拉取所有未出结果的任务最新状态 */
export async function syncInProgressTasksFromServer() {
  const list = loadDigitalHumanTasks()
  let changed = false
  for (const t of list) {
    if (!t.taskId || t.driveMode === 'legacy') continue
    const curHasResult = !!sanitizeVideoResultUrl(t.resultUrl || t.shuzirenUrl, t.cover)
    if (curHasResult) continue
    const curStatus = String(t.status || '').toLowerCase()
    if (curStatus === 'failed') continue
    try {
      const info = await queryImageHumanTask(t.taskId, {
        excludeUrls: [t.cover].filter(Boolean)
      })
      const nextUrl = sanitizeVideoResultUrl(info.resultUrl, t.cover)
      const nextStatus = info.status || t.status
      if (nextStatus === 'completed' || nextUrl) {
        upsertDigitalHumanTask({
          taskId: t.taskId,
          status: nextUrl ? 'completed' : nextStatus === 'completed' ? 'completed' : nextStatus,
          resultUrl: nextUrl || undefined
        })
        changed = true
      } else if (info.status === 'failed') {
        upsertDigitalHumanTask({
          taskId: t.taskId,
          status: 'failed',
          error: info.error || '生成失败'
        })
        changed = true
      } else if (info.status !== t.status) {
        upsertDigitalHumanTask({ taskId: t.taskId, status: info.status })
        changed = true
      }
    } catch (e) {
      /* 单次查询失败保留原状态 */
    }
  }
  return changed ? loadDigitalHumanTasks() : list
}

const _bgPollers = new Map()

/**
 * 后台续轮询（刷新/关页后再次进入可继续）
 * @param {string} taskId
 * @param {{ onUpdate?: (tasks: object[]) => void }} hooks
 */
export function resumeTaskPolling(taskId, hooks = {}) {
  const id = String(taskId || '').trim()
  if (!id || _bgPollers.has(id)) return

  const local = loadDigitalHumanTasks().find((t) => String(t.taskId) === id)
  const excludeUrls = local && local.cover ? [local.cover] : []

  const runner = (async () => {
    try {
      const done = await pollImageHumanUntilDone(id, {
        maxAttempts: 120,
        initialDelayMs: 4000,
        excludeUrls,
        onProgress: (info) => {
          upsertDigitalHumanTask({
            taskId: id,
            status: info.status,
            resultUrl: sanitizeVideoResultUrl(info.resultUrl, local?.cover) || undefined
          })
          if (typeof hooks.onUpdate === 'function') {
            hooks.onUpdate(loadDigitalHumanTasks())
          }
        }
      })
      upsertDigitalHumanTask({
        taskId: id,
        status: 'completed',
        resultUrl: sanitizeVideoResultUrl(done.resultUrl, local?.cover) || undefined
      })
    } catch (e) {
      const msg = e && e.message ? String(e.message) : '生成失败'
      if (/超时/.test(msg)) {
        upsertDigitalHumanTask({ taskId: id, status: 'processing', error: msg })
      } else {
        upsertDigitalHumanTask({ taskId: id, status: 'failed', error: msg })
      }
    } finally {
      _bgPollers.delete(id)
      if (typeof hooks.onUpdate === 'function') {
        hooks.onUpdate(loadDigitalHumanTasks())
      }
    }
  })()

  _bgPollers.set(id, runner)
}

/** 页面 mounted：云端拉取 + 同步 + 恢复所有进行中任务轮询 */
export async function bootstrapDigitalHumanTasks(hooks = {}) {
  if (!isLoggedInUser()) {
    const empty = []
    if (typeof hooks.onUpdate === 'function') hooks.onUpdate(empty)
    return empty
  }

  let list = loadDigitalHumanTasks()
  if (typeof hooks.onUpdate === 'function') hooks.onUpdate(list)

  list = await pullDigitalHumanTasksFromServer()
  if (typeof hooks.onUpdate === 'function') hooks.onUpdate(list)

  await syncInProgressTasksFromServer()
  list = loadDigitalHumanTasks()
  list
    .filter((t) => {
      if (t.driveMode === 'legacy') return false
      const status = String(t.status || '').toLowerCase()
      const hasResult = !!sanitizeVideoResultUrl(t.resultUrl || t.shuzirenUrl, t.cover)
      if (status === 'completed' && hasResult) return false
      return isTaskInProgress(t.status) || (status !== 'failed' && !hasResult)
    })
    .forEach((t) => resumeTaskPolling(t.taskId, hooks))

  if (typeof hooks.onUpdate === 'function') hooks.onUpdate(list)
  return loadDigitalHumanTasks()
}

export function statusLabel(status) {
  const s = String(status || '').toLowerCase()
  if (s === 'completed') return '已完成'
  if (s === 'failed') return '失败'
  if (s === 'pending') return '排队中'
  if (isTaskInProgress(s)) return '生成中'
  return '处理中'
}
